"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Flame, MousePointerClick, Move, ArrowDownWideNarrow, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { HeatmapConfig, HeatmapInstance } from "heatmap.js";
import type {
  HeatmapResponse,
  HeatmapCell as Cell,
  HeatmapScrollCell as ScrollCell,
} from "@/lib/heatmap/query";

const CELL = 16; // x/y 버킷 픽셀 (트래커와 동일해야 함)

type Device = "desktop" | "mobile";
type Kind = "click" | "move" | "scroll";

export interface HeatmapFetchParams {
  path?: string;
  device?: Device;
  width?: number;
  all?: boolean;
}
export type HeatmapFetcher = (params: HeatmapFetchParams) => Promise<HeatmapResponse>;

const KINDS: { key: Kind; label: string; icon: typeof Flame }[] = [
  { key: "click", label: "클릭", icon: MousePointerClick },
  { key: "move", label: "마우스 이동", icon: Move },
  { key: "scroll", label: "스크롤", icon: ArrowDownWideNarrow },
];

export function HeatmapExplorer({ fetcher }: { fetcher: HeatmapFetcher }) {
  const [index, setIndex] = useState<HeatmapResponse["index"]>([]);
  const [path, setPath] = useState<string>("");
  const [device, setDevice] = useState<Device>("desktop");
  const [width, setWidth] = useState<number>(0); // -1 = 전체 종합
  const [kind, setKind] = useState<Kind>("click");
  const [data, setData] = useState<HeatmapResponse | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(
    async (p?: string, d?: Device, w?: number) => {
      setLoading(true);
      try {
        const params: HeatmapFetchParams = p
          ? { path: p, device: d ?? "desktop", ...(w === -1 ? { all: true } : { width: w ?? 0 }) }
          : {};
        const payload = await fetcher(params);
        setData(payload);
        return payload;
      } finally {
        setLoading(false);
      }
    },
    [fetcher]
  );

  useEffect(() => {
    (async () => {
      const payload = await load();
      setIndex(payload.index ?? []);
      const first = payload.index?.[0];
      if (first) {
        setPath(first.path);
        setDevice(first.device);
        setWidth(-1); // 기본 = 전체 종합
        await load(first.path, first.device, -1);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const widthsFor = (p: string, d: Device) =>
    index.filter((i) => i.path === p && i.device === d).sort((a, b) => b.views - a.views);

  const select = (p: string, d: Device, w?: number) => {
    // 폭 미지정(페이지·기기 변경) 시 기본 = 전체 종합(-1)
    const chosen = w ?? -1;
    setPath(p);
    setDevice(d);
    setWidth(chosen);
    load(p, d, chosen);
  };

  const onPath = (p: string) => {
    const best = index.filter((i) => i.path === p).sort((a, b) => b.views - a.views)[0];
    select(p, best?.device ?? device);
  };

  const paths = Array.from(new Set(index.map((i) => i.path)));
  const widthOptions = widthsFor(path, device);
  const cells: Cell[] =
    kind === "click" ? data?.click ?? [] : kind === "move" ? data?.move ?? [] : [];

  if (index.length === 0 && !loading) {
    return (
      <Card className="p-6 text-sm text-muted-foreground">
        아직 수집된 히트맵 데이터가 없습니다. 공개 페이지 방문이 쌓이면 표시됩니다.
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
      <div className="space-y-4">
        <Card className="p-4 space-y-3">
          <div className="text-xs font-semibold text-muted-foreground">페이지</div>
          <div className="space-y-1 max-h-56 overflow-y-auto">
            {paths.map((p) => (
              <button
                key={p}
                onClick={() => onPath(p)}
                className={cn(
                  "w-full text-left text-sm px-2 py-1.5 rounded truncate",
                  p === path ? "bg-primary text-primary-foreground" : "hover:bg-muted"
                )}
                title={p}
              >
                {p}
              </button>
            ))}
          </div>
        </Card>

        <Card className="p-4 space-y-3">
          <div className="text-xs font-semibold text-muted-foreground">기기</div>
          <div className="flex gap-2">
            {(["desktop", "mobile"] as Device[]).map((d) => (
              <Button
                key={d}
                size="sm"
                variant={d === device ? "default" : "outline"}
                onClick={() => select(path, d)}
              >
                {d === "desktop" ? "데스크톱" : "모바일"}
              </Button>
            ))}
          </div>

          <div className="text-xs font-semibold text-muted-foreground pt-2">화면 폭 (방문자 수)</div>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant={width === -1 ? "default" : "outline"}
              onClick={() => select(path, device, -1)}
            >
              전체 종합
            </Button>
            {widthOptions.map((wo) => (
              <Button
                key={wo.width}
                size="sm"
                variant={wo.width === width ? "default" : "outline"}
                onClick={() => select(path, device, wo.width)}
              >
                {wo.width}px · {wo.views}
              </Button>
            ))}
            {widthOptions.length === 0 && (
              <span className="text-xs text-muted-foreground self-center">폭 데이터 없음</span>
            )}
          </div>

          <div className="text-xs font-semibold text-muted-foreground pt-2">히트맵 종류</div>
          <div className="flex flex-col gap-2">
            {KINDS.map((k) => (
              <Button
                key={k.key}
                size="sm"
                variant={k.key === kind ? "default" : "outline"}
                onClick={() => setKind(k.key)}
                className="justify-start gap-2"
              >
                <k.icon className="h-4 w-4" />
                {k.label}
              </Button>
            ))}
          </div>
        </Card>

        <Card className="p-4">
          <div className="text-xs text-muted-foreground">이 폭의 페이지뷰</div>
          <div className="text-2xl font-bold">{data?.pageviews ?? 0}</div>
        </Card>
      </div>

      <Card className="p-4 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <HeatmapViewer
            path={path}
            width={data?.width || 1280}
            kind={kind}
            cells={cells}
            scroll={data?.scroll ?? []}
            pageviews={data?.pageviews ?? 0}
          />
        )}
      </Card>
    </div>
  );
}

function HeatmapViewer({
  path,
  width,
  kind,
  cells,
  scroll,
  pageviews,
}: {
  path: string;
  width: number;
  kind: Kind;
  cells: Cell[];
  scroll: ScrollCell[];
  pageviews: number;
}) {
  const outerRef = useRef<HTMLDivElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const heatRef = useRef<HTMLDivElement>(null);
  const pollRef = useRef<number | null>(null);
  const [scale, setScale] = useState(1);
  const [measuredHeight, setMeasuredHeight] = useState(0);
  const [iframeOk, setIframeOk] = useState(true);

  const refWidth = width;
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const src = path ? `${origin}${path}` : "";
  const previewSrc = path
    ? `${origin}${path}${path.includes("?") ? "&" : "?"}hm_preview=1`
    : "";

  const dataExtent = Math.max(600, (cells.reduce((m, c) => Math.max(m, c.y), 0) + 6) * CELL);
  const renderHeight = measuredHeight > 0 ? measuredHeight : dataExtent;

  useEffect(() => {
    const compute = () => {
      const w = outerRef.current?.clientWidth || refWidth;
      setScale(w / refWidth);
    };
    compute();
    window.addEventListener("resize", compute);
    return () => window.removeEventListener("resize", compute);
  }, [refWidth]);

  const onIframeLoad = () => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc || !doc.body) {
      setIframeOk(false);
      setMeasuredHeight(0);
      return;
    }
    setIframeOk(true);
    try {
      const style = doc.createElement("style");
      style.setAttribute("data-heatmap-fix", "1");
      style.textContent =
        "html,body{min-height:0!important;height:auto!important}.min-h-screen{min-height:0!important}";
      doc.head.appendChild(style);
    } catch {
      /* ignore */
    }
    if (pollRef.current) clearTimeout(pollRef.current);
    let prev = -1;
    let stable = 0;
    let count = 0;
    const poll = () => {
      const h = doc.body?.scrollHeight ?? 0;
      if (h > 0) setMeasuredHeight((m) => Math.max(m, h));
      count += 1;
      stable = Math.abs(h - prev) <= 2 ? stable + 1 : 0;
      prev = h;
      if (stable >= 2 || count >= 10) return;
      pollRef.current = window.setTimeout(poll, 600);
    };
    poll();
  };

  useEffect(() => {
    setMeasuredHeight(0);
    return () => {
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [previewSrc, refWidth]);

  useEffect(() => {
    if (kind === "scroll") return;
    const container = heatRef.current;
    if (!container) return;
    container.innerHTML = "";
    if (cells.length === 0) return;

    let cancelled = false;
    (async () => {
      try {
        const mod = (await import("heatmap.js")) as unknown as {
          default?: { create?: (c: HeatmapConfig) => HeatmapInstance };
          create?: (c: HeatmapConfig) => HeatmapInstance;
        };
        const create = mod.default?.create ?? mod.create;
        if (cancelled || !heatRef.current || typeof create !== "function") {
          if (typeof create !== "function")
            console.warn("[heatmap] create() 미발견 — 모듈 형태 확인 필요");
          return;
        }
        const inst = create({
          container: heatRef.current,
          radius: kind === "move" ? 22 : 32,
          maxOpacity: 0.6,
          minOpacity: 0,
          blur: 0.78,
        });
        let max = 0;
        const points = cells.map((c) => {
          max = Math.max(max, c.w);
          return {
            x: Math.round((c.x + 0.5) * CELL),
            y: Math.round((c.y + 0.5) * CELL),
            value: c.w,
          };
        });
        inst.setData({ max: Math.max(max, 1), min: 0, data: points });
      } catch (e) {
        console.warn("[heatmap] render failed", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [cells, kind, refWidth, renderHeight]);

  const scrollTotal = scroll.reduce((s, c) => s + c.w, 0);
  const reachAt = (d: number) =>
    scrollTotal === 0
      ? 0
      : scroll.filter((c) => c.depth >= d).reduce((s, c) => s + c.w, 0) / scrollTotal;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between text-sm">
        <span className="font-mono text-muted-foreground truncate">
          {src ? `${src} (${refWidth}px)` : "(페이지 없음)"}
        </span>
        <span className="text-xs text-muted-foreground shrink-0 ml-2">
          {kind === "scroll"
            ? `스크롤 세션 ${scrollTotal}`
            : `${cells.length} 셀 · 페이지뷰 ${pageviews}`}
        </span>
      </div>

      {!iframeOk && (
        <p className="text-xs text-amber-600">
          페이지 미리보기를 불러올 수 없어(다른 출처/차단) 좌표 격자 위에만 표시합니다.
        </p>
      )}

      <div
        ref={outerRef}
        className="border rounded-lg overflow-auto bg-muted/20"
        style={{ maxHeight: "70vh" }}
      >
        <div style={{ height: renderHeight * scale }}>
          <div
            ref={wrapRef}
            className="relative origin-top-left"
            style={{ width: refWidth, height: renderHeight, transform: `scale(${scale})` }}
          >
            {previewSrc && (
              <iframe
                ref={iframeRef}
                src={previewSrc}
                onLoad={onIframeLoad}
                title="page-preview"
                scrolling="no"
                style={{
                  width: refWidth,
                  height: renderHeight,
                  border: 0,
                  display: iframeOk ? "block" : "none",
                  background: "#fff",
                  pointerEvents: "none",
                  overflow: "hidden",
                }}
              />
            )}
            {!iframeOk && (
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage:
                    "linear-gradient(#0000000a 1px, transparent 1px), linear-gradient(90deg, #0000000a 1px, transparent 1px)",
                  backgroundSize: `${CELL * 4}px ${CELL * 4}px`,
                }}
              />
            )}

            {kind !== "scroll" && (
              <div
                className="absolute inset-0 pointer-events-none overflow-hidden"
                style={{ width: refWidth, height: renderHeight }}
              >
                <div ref={heatRef} style={{ width: refWidth, height: renderHeight }} />
              </div>
            )}

            {kind === "scroll" && (
              <div className="absolute inset-0 pointer-events-none">
                {Array.from({ length: 20 }, (_, i) => {
                  const dLow = i * 5;
                  const reach = reachAt(dLow);
                  return (
                    <div
                      key={i}
                      className="absolute left-0 right-0 flex items-center"
                      style={{
                        top: (dLow / 100) * renderHeight,
                        height: renderHeight / 20,
                        background: `rgba(220, 38, 38, ${0.05 + reach * 0.5})`,
                      }}
                    >
                      <span className="text-[10px] text-white font-semibold bg-black/40 px-1 ml-1 rounded">
                        {dLow}% · {Math.round(reach * 100)}% 도달
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
